import React, { FC, HTMLAttributes, useEffect } from 'react'
import classNames from 'classnames'
import { Case, Switch, When } from 'react-if'
import { ProgressButton } from '@/atoms/Button'
import { GuildCopperIcon } from '@/atoms/Icons/GuildCopperIcon'
import { PayItForwardIcon } from '@/atoms/Icons/PayItForwardIcon'
import { Image } from '@/atoms/Image'
import { LegacyImage } from '@/atoms/LegacyImage'
import { CaptionMD, LabelLG, TitleSM, ParagraphSM } from '@/atoms/Text'
import { Avatar } from '@/molecules/Avatar'
import { Asset } from '@/organisms/AngelPlayer'
import {
  createVideoTrackEvent,
  PifAttributionEventName,
} from '@/organisms/AngelPlayer/AngelPlayerContext/trackEventFactory'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { Benefactor, UserProfile } from '@/views/WatchEpisodeView/ShowAttribution/types'

interface ShowAttributionPropsFullscreen extends HTMLAttributes<HTMLDivElement> {
  benefactor?: Benefactor
  onThankYouClick?: () => void
  clearTimeouts: () => void
  asset: Asset
  poster: string
  projectTitle: string | undefined
}

export const ShowAttributionFullscreen: FC<ShowAttributionPropsFullscreen> = React.memo(
  ({ asset, benefactor, className, onThankYouClick, clearTimeouts, poster, projectTitle, ...props }) => {
    const track = useSafeTrack()
    const { t } = useTranslate('watch')

    useEffect(() => {
      track('PIF Attribution Viewed', {
        ...createVideoTrackEvent(PifAttributionEventName.PifAttributionViewed, asset),
        attributed_by: {
          region: benefactor?.country,
          user_displayed_name: benefactor?.firstName,
        },
      })
    }, [asset, benefactor?.country, benefactor?.firstName, track])

    const { user } = benefactor || {}
    const { profile = {} as UserProfile } = user || {}

    const firstName = benefactor?.firstName
    const profileImage = profile?.image || ''

    const userName = firstName || t('aFan', 'a fan')
    const country = benefactor?.country
    const city = benefactor?.city
    const state = benefactor?.state
    const hasLocation = Boolean(city && state)

    const handleSkipAttribution = () => {
      const fullscreen = document.getElementById('fullscreen-attribution')
      clearTimeouts()
      fullscreen?.classList.add('hidden')
      track('Skipped Attribution', {
        ...createVideoTrackEvent(PifAttributionEventName.PifAttributionViewed, asset),
      })
    }

    return (
      <div
        className={classNames('fixed inset-0 z-[99] w-full h-full md:absolute', className)}
        id="fullscreen-attribution"
      >
        <LegacyImage
          alt="Episode Poster Image"
          className="z-[-1]"
          layout="fill"
          objectFit="cover"
          objectPosition="center"
          src={`${poster}.webp`}
        />
        <div className="bg-opacity/20 flex h-full w-full items-center justify-center bg-black backdrop-blur-3xl">
          <div className="relative w-full align-middle">
            <div
              className="
              absolute
              inset-1/2
              z-20
              flex
              h-min
              w-full
              max-w-lg
              flex-none
              -translate-x-1/2
              -translate-y-1/2
              flex-col
              items-center
              justify-center
              gap-3
              overflow-hidden
              p-8
              transition-all
              duration-[2000ms]
              sm:z-0
              md:w-max
              md:items-start"
              {...props}
            >
              <div className="flex flex-row items-center gap-4 self-center">
                <Switch>
                  <Case condition={firstName && hasLocation}>
                    <GuildCopperIcon size={35} />
                    <CaptionMD color="core-gray-500" weight="medium" className="md:photon-caption-lg">
                      {t('angelGuildMemberFrom', 'Guild Member from {{ city }}, {{ state }}', {
                        city,
                        state,
                      })}
                    </CaptionMD>
                  </Case>
                  <Case condition={firstName && !hasLocation}>
                    <GuildCopperIcon size={35} />
                    <CaptionMD color="core-gray-500" weight="medium" className="md:photon-caption-lg">
                      {t('angelGuildMember', 'Angel Guild Member')}
                    </CaptionMD>
                  </Case>
                  <Case condition={!firstName}>
                    <div className="flex flex-row items-center gap-4 px-4">
                      <PayItForwardIcon color="oxide-bright" />
                      <CaptionMD color="core-gray-500" weight="medium" className="md:photon-caption-lg">
                        {t('anAngelFanPIF', 'An Angel fan Paid It Forward for you to watch free')}
                      </CaptionMD>
                    </div>
                  </Case>
                </Switch>
              </div>
              <When condition={firstName}>
                <div className="flex w-full flex-row items-center justify-center gap-2 md:gap-3">
                  <Avatar size={36} src={profileImage} className="mr-2" />
                  <When condition={country}>
                    <Image
                      alt="country"
                      src={`${country?.toLowerCase() ?? 'us'}.webp`}
                      width={16}
                      height={12}
                      quality={100}
                      loader={({ src }) => `https://flagcdn.com/32x24/${src}`}
                    />
                  </When>
                  <TitleSM color="white" weight="bold" className="md:photon-title-lg">
                    {userName}
                  </TitleSM>
                </div>
                <ParagraphSM color="core-gray-500" weight="medium" className="md:photon-paragraph-md text-center">
                  {t('madeThisFree', 'made this free for you to watch.')}
                </ParagraphSM>
              </When>
              <ProgressButton
                className="my-6 w-[240px] self-center !text-center font-semibold capitalize text-gray-950"
                variant="oxide-bright"
                onClick={onThankYouClick}
              >
                <LabelLG>{t('sayThankYou', ' Say thank you')}</LabelLG>
              </ProgressButton>
              <div className="cursor mx-auto my-1 cursor-pointer text-center text-sm" onClick={handleSkipAttribution}>
                <Translate t={t} i18nKey="startWatching">
                  Start Watching
                </Translate>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
)

ShowAttributionFullscreen.displayName = 'React.memo(ShowAttributionFullscreen)'
